
// Revisado: 2025-12-18 I18n Updated
import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../constants';
import { useTranslation } from 'react-i18next';

const SupportSkillsEdit = ({ supportId, currentSkills, onUpdate }) => {
    const { t } = useTranslation();
    // Local state for skills to manage deletions/additions before saving
    const [localSkills, setLocalSkills] = useState(currentSkills);
    const [originalSkills, setOriginalSkills] = useState(currentSkills);

    // Search State
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);

    // UI State
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);

    // Sync local state when props change
    useEffect(() => {
        setLocalSkills(currentSkills);
        setOriginalSkills(currentSkills);
        setHasChanges(false);
    }, [currentSkills]);

    // Check for changes
    useEffect(() => {
        // Simple array comparison (assuming order doesn't matter for change detection, checking sets of IDs)
        const currentIds = localSkills.map(s => s.id_skill || s.Id_skill).sort((a, b) => a - b);
        const originalIds = originalSkills.map(s => s.id_skill || s.Id_skill).sort((a, b) => a - b);

        const isDifferent = JSON.stringify(currentIds) !== JSON.stringify(originalIds);
        setHasChanges(isDifferent);
    }, [localSkills, originalSkills]);

    // Debounced Search
    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (searchTerm.length > 2) {
                setSearching(true);
                try {
                    const response = await fetch(`${API_BASE_URL}/api/skills/search?q=${encodeURIComponent(searchTerm)}`);

                    // Defensive check for JSON
                    const contentType = response.headers.get("content-type");
                    if (!contentType || !contentType.includes("application/json")) {
                        throw new Error(t('SupportSkillsEdit.server_error'));
                    }

                    if (!response.ok) throw new Error(t('SupportSkillsEdit.search_failed'));

                    const data = await response.json();
                    setSearchResults(data);
                } catch (err) {
                    console.error("Search failed", err);
                    setSearchResults([]);
                } finally {
                    setSearching(false);
                }
            } else {
                setSearchResults([]);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, t]);

    // Add Skill (Local)
    const handleAddSkill = (skill) => {
        const skillId = skill.Id_skill;
        const alreadyExists = localSkills.some(s => (s.id_skill || s.Id_skill) === skillId);

        if (alreadyExists) return;

        // Normalizing skill object structure to match what usually comes from DB for display
        // Note: API returns id_skill in support_skills, but search returns Id_skill
        const newSkill = {
            id_skill: skillId,
            Id_skill: skillId, // Keep both for safety
            icon_id: skill.icon_id,
            skill_name: skill.Skill_name,
            skill_name_jp: skill.Skill_name_jp
        };

        setLocalSkills([...localSkills, newSkill]);
        setSearchTerm('');
        setSearchResults([]);
    };

    // Remove Skill (Local)
    const handleRemoveSkill = (skillId) => {
        setLocalSkills(localSkills.filter(s => (s.id_skill || s.Id_skill) !== skillId));
    };

    // Save Changes (Logic)
    const handleSave = async () => {
        if (!window.confirm(t('SupportSkillsEdit.confirm_save'))) return;

        setSaving(true);
        setError(null);
        setSuccess(null);

        // Normalize IDs
        const getIds = (list) => list.map(s => s.id_skill || s.Id_skill);
        const originalIdsList = getIds(originalSkills);
        const currentIdsList = getIds(localSkills);

        const originalIdsSet = new Set(originalIdsList);
        const currentIdsSet = new Set(currentIdsList);

        const toAdd = currentIdsList.filter(id => !originalIdsSet.has(id));
        const toRemove = originalIdsList.filter(id => !currentIdsSet.has(id));

        try {
            // Processing Adds
            for (const skillId of toAdd) {
                await fetch(`${API_BASE_URL}/api/supports/${supportId}/skills`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ id_skill: skillId })
                });
            }

            // Processing Removes
            for (const skillId of toRemove) {
                await fetch(`${API_BASE_URL}/api/supports/${supportId}/skills/${skillId}`, {
                    method: 'DELETE', // Keep DELETE method
                    headers: {
                        'Content-Type': 'application/json'
                    },
                });
            }

            setSuccess(t('SupportSkillsEdit.skills_updated'));
            setHasChanges(false); // Reset changes flag

            // Allow parent to refresh to get canonical state from DB
            if (onUpdate) await onUpdate();

        } catch (err) {
            setError(t('SupportSkillsEdit.save_error_prefix') + err.message);
        } finally {
            setSaving(false);
        }
    };

    const getSkillIconPath = (iconId) => `/images/Icon/Skill_Icons/icon_skill_${iconId}.png`;

    return (
        <div className="space-y-6">

            {/* Messages */}
            {error && <div className="text-red-600 bg-red-50 p-2 rounded">{error}</div>}
            {success && <div className="text-green-600 bg-green-50 p-2 rounded">{success}</div>}

            {/* Header with Save Button */}
            <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg border">
                <span className="font-semibold text-gray-700">{t('SupportSkillsEdit.header_skills_count', { count: localSkills.length })}</span>
                <button
                    onClick={handleSave}
                    disabled={!hasChanges || saving}
                    className={`px-4 py-2 rounded font-bold text-white transition-colors ${!hasChanges || saving
                        ? 'bg-gray-400 cursor-not-allowed opacity-50'
                        : 'bg-green-600 hover:bg-green-700'
                        }`}
                >
                    {saving ? t('SupportSkillsEdit.saving') : t('SupportSkillsEdit.save_changes')}
                </button>
            </div>

            {/* Current Skills List */}
            <div>
                {localSkills.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {localSkills.map((skill) => (
                            <div key={skill.id_skill || skill.Id_skill} className="flex items-center justify-between p-3 border rounded-lg bg-white shadow-sm">
                                <div className="flex items-center gap-3">
                                    <img
                                        src={getSkillIconPath(skill.icon_id)}
                                        alt=""
                                        className="w-10 h-10 object-contain"
                                        onError={(e) => { e.target.src = '/images/placeholder.png'; }}
                                    />
                                    <div>
                                        <div className="font-bold text-gray-800">{skill.skill_name || skill.Skill_name || t('SupportSkillsEdit.unknown_skill')}</div>
                                        <div className="text-xs text-gray-500">{skill.skill_name_jp || skill.Skill_name_jp}</div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleRemoveSkill(skill.id_skill || skill.Id_skill)}
                                    className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50"
                                    title="Remove Skill"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-500 italic p-4 text-center border-2 border-dashed rounded-lg">{t('SupportSkillsEdit.no_skills_assigned')}</p>
                )}
            </div>

            {/* Add Skill Search */}
            <div className="mt-8 border-t pt-6">
                <h3 className="text-lg font-semibold mb-3 text-gray-700">{t('SupportSkillsEdit.add_new_skill')}</h3>
                <div className="relative">
                    <input
                        type="text"
                        placeholder={t('SupportSkillsEdit.search_placeholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full p-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    {searching && (
                        <div className="absolute right-3 top-3">
                            <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    )}

                    {/* Search Results Dropdown */}
                    {searchResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-xl max-h-60 overflow-y-auto">
                            {searchResults.map((skill) => {
                                // Check if already added (local state)
                                const isAdded = localSkills.some(s => (s.id_skill || s.Id_skill) === skill.Id_skill);

                                return (
                                    <div
                                        key={skill.Id_skill}
                                        className={`flex items-center justify-between p-3 border-b last:border-b-0 ${isAdded ? 'bg-gray-100 opacity-50 cursor-not-allowed' : 'hover:bg-blue-50 cursor-pointer'}`}
                                        onClick={() => !isAdded && handleAddSkill(skill)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <img
                                                src={getSkillIconPath(skill.icon_id)}
                                                alt=""
                                                className="w-8 h-8 object-contain"
                                                onError={(e) => { e.target.src = '/images/placeholder.png'; }}
                                            />
                                            <div>
                                                <div className="font-semibold text-sm text-gray-800">{skill.Skill_name}</div>
                                                <div className="text-xs text-gray-500">{skill.Skill_name_jp}</div>
                                            </div>
                                        </div>
                                        {isAdded ? (
                                            <span className="text-xs text-gray-500 font-medium px-2 py-1 bg-gray-200 rounded">{t('SupportSkillsEdit.status_added')}</span>
                                        ) : (
                                            <span className="text-xs text-white font-medium px-2 py-1 bg-green-500 rounded hover:bg-green-600">{t('SupportSkillsEdit.button_add')}</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
};

export default SupportSkillsEdit;
// Revisado: 2025-12-16
